<template>
	<page-template>
		<template #content>
			<v-container fluid>
				<v-row class="px-4 pt-16" v-if="company">
					<v-col cols="12">
						<v-card class="ant-card px-4 py-2" outlined>
							<v-row>
								<v-col cols="2" class="d-flex align-center justify-center">
									<v-avatar size="120">
										<v-img v-if="logoUrl" :src="logoUrl" height="120px" width="120px">
											<template #placeholder>
												<v-row class="fill-height ma-0" align="center" justify="center">
													<v-progress-circular indeterminate />
												</v-row>
											</template>
										</v-img>
										<v-icon v-else size="80">icon-image</v-icon>
									</v-avatar>
								</v-col>
								<v-col cols="10">
									<v-card-title class="headline pb-2">
										{{ company.name }}
									</v-card-title>

									<v-divider class="mb-4" />

									<v-card-text>
										<v-row>
											<v-col cols="12" sm="6" class="py-2">
												<div>
													<strong>{{ $t("country") }}:</strong>
													{{ getCountryName(company.countryId) }}
												</div>
												<div v-if="company.website">
													<strong>{{ $t("website") }}:</strong>
													{{ company.website }}
												</div>
												<div v-if="company.phoneNumber">
													<strong>{{ $t("phone_number") }}:</strong>
													{{ company.phoneNumber }}
												</div>
											</v-col>

											<v-col cols="12" sm="6" class="py-2">
												<div v-if="company.email">
													<strong>{{ $t("email") }}:</strong>
													{{ company.email }}
												</div>
												<div v-if="company.address">
													<strong>{{ $t("address") }}:</strong>
													{{ company.address }}
												</div>
											</v-col>
										</v-row>
									</v-card-text>
								</v-col>
							</v-row>
						</v-card>
					</v-col>
				</v-row>

				<v-row class="px-4">
					<v-col v-for="stock in stocks" :key="stock._id" cols="12" sm="6" md="4" lg="3">
						<v-card class="pointer ant-card" outlined @click="goToStock(stock._id)">
							<template v-if="stockImages[stock._id]">
								<v-img :src="stockImages[stock._id]" height="200px"></v-img>
							</template>
							<template v-else>
								<div class="no-image-container">
									<span>{{ $t("no_image") }}</span>
								</div>
							</template>

							<v-card-text class="text-center">
								{{ getProductName(stock.productId?.name) }}
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
			</v-container>
		</template>
	</page-template>
</template>

<script>
import remote from "../../../data/remote";
import { mapState } from "vuex";
import PageTemplate from "../../templates/PageTemplate.vue";

export default {
	name: "PublicStocks",
	data() {
		return {
			company: null,
			stocks: [],
			loading: false,
			stockImages: {},
			logoUrl: null,
		};
	},
	components: {
		PageTemplate,
	},
	computed: {
		...mapState(["countries"]),
	},
	methods: {
		backAction() {
			this.$router.go(-1);
		},
		fetchData() {
			this.loading = true;
			const publicUrl = this.$route.params.publicUrl;
			remote.companies.getPublicCompany(publicUrl, {
				onSuccess: async (result) => {
					this.company = result.company;
					this.loadCompanyLogo();
					const allStocks = result.stocks;
					const filteredStocks = [];
					for (const stock of allStocks) {
						const isVisible = await this.checkStockVisibility(stock);
						if (isVisible) {
							filteredStocks.push(stock);
						}
					}
					this.stocks = filteredStocks;
					await this.loadFirstImages();
					this.loading = false;
				},
				onFail: (error) => {
					console.error(error);
					this.loading = false;
				},
			});
		},
		checkStockVisibility(stock) {
			return new Promise((resolve) => {
				remote.companies.getPublicStock(stock._id, {
					onSuccess: (result) => {
						const batches = result.batches;
						if (batches && batches.length) {
							const visibleBatch = batches.find((batch) => batch.visibility === true);
							resolve(!!visibleBatch);
						} else {
							resolve(false);
						}
					},
					onFail: (error) => {
						console.error("Error fetching batches for stock:", stock._id, error);
						resolve(false);
					},
				});
			});
		},
		async loadFirstImages() {
			for (const stock of this.stocks) {
				if (stock.images && stock.images.length) {
					try {
						const firstKey = stock.images[0];
						const blob = await this.loadStockImageBlob(firstKey);
						const url = window.URL.createObjectURL(blob);
						this.$set(this.stockImages, stock._id, url);
					} catch (err) {
						console.error("Failed to load image for stock:", stock._id, err);
					}
				}
			}
		},
		loadStockImageBlob(imgKey) {
			return new Promise((resolve, reject) => {
				remote.files.get(imgKey, {
					onSuccess: (result) => {
						const blob = new Blob([result]);
						resolve(blob);
					},
					onFail: (error) => reject(error),
				});
			});
		},
		getProductName(nameObj) {
			if (!nameObj) return "Unnamed Stock";
			const userLang = (this.$i18n && this.$i18n.locale) || (navigator.language || "en").split("-")[0] || "en";
			if (nameObj[userLang]) return nameObj[userLang];
			if (nameObj._default) return nameObj._default;
			return "Unnamed Stock";
		},
		getCountryName(countryId) {
			if (!this.countries) return "";
			const country = this.countries.find((c) => c.id === countryId);
			return country ? `${this.$t("$country." + country.name)} (${country.code})` : "";
		},
		goToStock(stockId) {
			this.$router.push({
				name: "publicStockDetails",
				params: {
					publicUrl: this.$route.params.publicUrl,
					stockId,
				},
			});
		},
		loadCompanyLogo() {
			if (this.company && this.company.logo) {
				remote.files.get(this.company.logo.key, {
					onSuccess: (result) => {
						this.logoUrl = window.URL.createObjectURL(new Blob([result]));
					},
					onFail: (error) => {
						console.error("Failed to load logo:", error);
					},
				});
			}
		},
	},
	created() {
		this.fetchData();
	},
};
</script>

<style scoped>
.pointer {
	cursor: pointer;
}
.no-image-container {
	height: 200px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #eee;
}
</style>
